import { ReactNode } from 'react';

import LNB from '@/components/common/LNB';
import {
  ALL_TYPES,
  BizTargetType,
  DEALER_TYPE,
  DIRECT_PARTNER_TYPE,
  PARTNER_TYPES,
  SETTLEABLE_TYPES
} from '@/constants/bizTargetType';
import { ALL_BIZ_TYPES, BizType, STATS_REPORT_BIZ_TYPES } from '@/constants/bizType';
import { ALL_ROLES, CONTRACT_MANAGABLE_ROLES, PARTNER_MANAGABLE_ROLES, Role, SETTLEABLE_ROLES } from '@/constants/role';
import { PARTNER_ID, ROUTES } from '@/constants/routes';
import usePrefixNavigate from '@/hooks/usePrefixNavigate';
import { ReportPeriod } from '@/pages/:AUTH_INFO_ID/stats/report/types/ReportPeriod';
import { useAuthStore } from '@/store/auth';
import { getTabUrl } from '@/utils/report';

type ConditionalMenuProps = {
  allowedTypes?: BizTargetType[];
  allowedBizTypes?: BizType[];
  allowedRoles?: Role[];
  children: ReactNode;
};

const ConditionalMenu = ({
  allowedTypes = ALL_TYPES,
  allowedBizTypes = ALL_BIZ_TYPES,
  allowedRoles = ALL_ROLES,
  children
}: ConditionalMenuProps) => {
  const { bizTargetType, role, bizType } = useAuthStore();

  const isAllowedBizTargetType = bizTargetType && allowedTypes.includes(bizTargetType);
  const isAllowedRole = role && allowedRoles.includes(role);
  const isAllowedBizType = bizType && allowedBizTypes.includes(bizType);

  if (!isAllowedBizTargetType || !isAllowedRole || !isAllowedBizType) return null;

  return <>{children}</>;
};

const LNBMenu = () => {
  const { partnerId, bizType } = useAuthStore();
  const { linkTo } = usePrefixNavigate();

  return (
    <LNB.Menu>
      <ConditionalMenu>
        <LNB.MenuItem iconClassName="ico_wallet ico_snb1" to={linkTo(ROUTES.DASHBOARD)}>
          대시보드
        </LNB.MenuItem>
      </ConditionalMenu>

      <ConditionalMenu allowedRoles={PARTNER_MANAGABLE_ROLES}>
        <LNB.SubMenu
          title="이용기관 관리"
          iconClassName="ico_wallet ico_snb2"
          matchedPath={Object.values({ ...ROUTES.PARTNER, ...ROUTES.APP })}
        >
          <ConditionalMenu allowedTypes={DEALER_TYPE}>
            <LNB.SubMenuItem
              additionalActivePathList={[
                ROUTES.APP.REGISTER,
                ROUTES.APP.CREATE,
                ROUTES.PARTNER.REGISTER,
                ROUTES.PARTNER.DETAIL,
                ROUTES.PARTNER.ADD_SVC,
                ROUTES.PARTNER.SVC_DETAIL,
                ROUTES.PARTNER.EDIT_SVC
              ]}
              to={linkTo(ROUTES.PARTNER.INDEX)}
            >
              이용기관 목록
            </LNB.SubMenuItem>
          </ConditionalMenu>
          <ConditionalMenu allowedTypes={PARTNER_TYPES}>
            <LNB.SubMenuItem
              additionalActivePathList={[ROUTES.PARTNER.SVC_DETAIL, ROUTES.PARTNER.EDIT_SVC]}
              to={linkTo(ROUTES.PARTNER.SVC, { [PARTNER_ID]: partnerId })}
            >
              기관 목록
            </LNB.SubMenuItem>
          </ConditionalMenu>
        </LNB.SubMenu>
      </ConditionalMenu>

      <ConditionalMenu>
        <LNB.SubMenu title="정보관리" iconClassName="ico_wallet ico_snb3" matchedPath={Object.values(ROUTES.INFO)}>
          <LNB.SubMenuItem to={linkTo(ROUTES.INFO.MEMBERS)}>멤버 관리</LNB.SubMenuItem>
          <LNB.SubMenuItem to={linkTo(ROUTES.INFO.BUSINESS)}>사업자 정보 관리</LNB.SubMenuItem>
        </LNB.SubMenu>
      </ConditionalMenu>

      <ConditionalMenu allowedRoles={CONTRACT_MANAGABLE_ROLES}>
        <LNB.SubMenu title="계약관리" iconClassName="ico_wallet ico_snb4" matchedPath={Object.values(ROUTES.CONTRACT)}>
          <ConditionalMenu allowedTypes={DEALER_TYPE}>
            <LNB.SubMenuItem to={linkTo(ROUTES.CONTRACT.DEALER)}>딜러계약 조회</LNB.SubMenuItem>
            <LNB.SubMenuItem
              to={linkTo(ROUTES.CONTRACT.PARTNER_FOR_DEALER)}
              additionalActivePathList={[ROUTES.CONTRACT.PARTNER_DETAIL]}
            >
              이용기관 계약조회
            </LNB.SubMenuItem>
            <LNB.SubMenuItem to={linkTo(ROUTES.CONTRACT.ADD)}>이용기관 계약추가</LNB.SubMenuItem>
          </ConditionalMenu>
          <ConditionalMenu allowedTypes={PARTNER_TYPES}>
            <LNB.SubMenuItem
              to={linkTo(ROUTES.CONTRACT.PARTNER_FOR_PARTNER)}
              additionalActivePathList={[ROUTES.CONTRACT.PARTNER_DETAIL]}
            >
              계약조회
            </LNB.SubMenuItem>
          </ConditionalMenu>
          <ConditionalMenu allowedTypes={DIRECT_PARTNER_TYPE}>
            <LNB.SubMenuItem to={linkTo(ROUTES.CONTRACT.ADD)}>계약추가</LNB.SubMenuItem>
          </ConditionalMenu>
        </LNB.SubMenu>
      </ConditionalMenu>

      <ConditionalMenu allowedTypes={SETTLEABLE_TYPES} allowedRoles={SETTLEABLE_ROLES}>
        <LNB.SubMenu title="정산관리" iconClassName="ico_wallet ico_snb5" matchedPath={Object.values(ROUTES.SETTLE)}>
          <LNB.SubMenuItem to={linkTo(ROUTES.SETTLE.INVOICE)} additionalActivePathList={[ROUTES.SETTLE.INVOICE_DETAIL]}>
            정산내역
          </LNB.SubMenuItem>
          <LNB.SubMenuItem to={linkTo(ROUTES.SETTLE.TAX_INVOICE_ISSUANCE)}>세금계산서 발행 내역</LNB.SubMenuItem>
          <LNB.SubMenuItem to={linkTo(ROUTES.SETTLE.BASE_INFO)}>정산 기본 정보</LNB.SubMenuItem>
        </LNB.SubMenu>
      </ConditionalMenu>

      <ConditionalMenu>
        <LNB.SubMenu
          title="통계"
          iconClassName="ico_wallet ico_snb6"
          matchedPath={Object.values(ROUTES.STATS)}
          showBadge={bizType !== null && STATS_REPORT_BIZ_TYPES.includes(bizType)}
        >
          <ConditionalMenu allowedBizTypes={STATS_REPORT_BIZ_TYPES}>
            <LNB.SubMenuItem to={linkTo(`${ROUTES.STATS.REPORT}${getTabUrl(ReportPeriod.WEEK)}`)}>
              통계 리포트
            </LNB.SubMenuItem>
          </ConditionalMenu>
          <LNB.SubMenuItem to={linkTo(ROUTES.STATS.USAGE)}>사용량 조회</LNB.SubMenuItem>
        </LNB.SubMenu>
      </ConditionalMenu>
    </LNB.Menu>
  );
};

export default LNBMenu;
